/* ELLISBROOK theme behaviour: header, drawers, collection paging, recommendations, product page. */
(() => {
  const mqDesktop = window.matchMedia('(min-width: 750px)');
  const closeIcon =
    '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  /* ---------- Header ---------- */

  const shell = document.querySelector('[data-bm-header]');
  if (shell) {
    const transparent = shell.classList.contains('bm-header-shell--transparent');
    const onScroll = () => {
      const y = window.scrollY;
      shell.classList.toggle('is-scrolled', y > 4);
      if (transparent) shell.classList.toggle('is-solid', y > 40 || shell.matches(':hover'));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    if (transparent) {
      shell.addEventListener('mouseenter', () => shell.classList.add('is-solid'));
      shell.addEventListener('mouseleave', onScroll);
    }
  }

  /* ---------- Drawers: menu and filters ---------- */

  const openDrawer = (drawer, trigger) => {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bm-lock');
    drawer.bmTrigger = trigger || null;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    const focusable = drawer.querySelector('button, a[href], input:not([type="hidden"])');
    if (focusable) focusable.focus({ preventScroll: true });
  };

  const closeDrawer = (drawer) => {
    if (!drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bm-lock');
    if (drawer.bmTrigger) {
      drawer.bmTrigger.setAttribute('aria-expanded', 'false');
      drawer.bmTrigger.focus({ preventScroll: true });
    }
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-bm-drawer-open], [data-bm-filter-open]');
    if (opener) {
      const drawer = document.getElementById(opener.getAttribute('aria-controls'));
      if (drawer) openDrawer(drawer, opener);
      return;
    }

    const closer = event.target.closest('[data-bm-drawer-close], [data-bm-filter-close]');
    if (closer) {
      const drawer = closer.closest('[data-bm-drawer], [data-bm-filter]');
      if (drawer) closeDrawer(drawer);
    }

    document.querySelectorAll('.bm-sort[open]').forEach((sort) => {
      if (!sort.contains(event.target)) sort.removeAttribute('open');
    });
  });

  /* ---------- Photo zoom ---------- */

  const closeZoom = () => {
    const viewer = document.querySelector('.bm-zoom.is-open');
    if (!viewer) return;
    viewer.classList.remove('is-open');
    document.body.classList.remove('bm-lock');
    if (viewer.bmTrigger) viewer.bmTrigger.focus({ preventScroll: true });
  };

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-bm-drawer].is-open, [data-bm-filter].is-open').forEach(closeDrawer);
    document.querySelectorAll('.bm-sort[open]').forEach((sort) => sort.removeAttribute('open'));
    closeZoom();
  });

  /* ---------- Collection: load more ---------- */

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-bm-more-btn]');
    if (!button) return;
    event.preventDefault();
    if (button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');

    try {
      const response = await fetch(button.href, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Status ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = document.getElementById('BmCollGrid');
      const nextGrid = doc.getElementById('BmCollGrid');
      if (grid && nextGrid) {
        nextGrid.querySelectorAll(':scope > .bm-card').forEach((card) => grid.appendChild(card));
      }
      const more = button.closest('[data-bm-more]');
      const nextMore = doc.querySelector('[data-bm-more]');
      if (nextMore) {
        more.replaceWith(nextMore);
      } else {
        more.remove();
      }
    } catch (error) {
      window.location.href = button.href;
    }
  });

  /* ---------- Product recommendations ---------- */

  document.querySelectorAll('[data-bm-recs]').forEach(async (holder) => {
    try {
      const response = await fetch(holder.dataset.url);
      if (!response.ok) throw new Error(`Status ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const recs = doc.querySelector('.bm-recs');
      if (recs) {
        holder.replaceWith(recs);
      } else {
        holder.remove();
      }
    } catch (error) {
      holder.remove();
    }
  });

  /* ---------- Product page ---------- */

  class BmProduct extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-bm-variants-json]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.form = this.querySelector('form[data-bm-form]');
      this.idInput = this.querySelector('[data-bm-variant-id]');
      this.gallery = this.querySelector('[data-bm-gallery]');
      this.counter = this.querySelector('[data-bm-counter]');

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-bm-option]')) this.onOptionChange();
      });

      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));

      if (this.gallery) {
        this.gallery.addEventListener('scroll', () => this.updateCounter(), { passive: true });
        this.gallery.addEventListener('click', (event) => {
          const item = event.target.closest('.bm-media[data-zoom-src]');
          if (item && event.target.tagName === 'IMG' && mqDesktop.matches) this.openZoom(item);
        });
      }
    }

    selectedOptions() {
      return [...this.querySelectorAll('.bm-opt')].map((group) => {
        const checked = group.querySelector('[data-bm-option]:checked');
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const selected = this.selectedOptions();
      this.querySelectorAll('.bm-opt').forEach((group, index) => {
        const label = group.querySelector('[data-bm-opt-value]');
        if (label) label.textContent = selected[index] || '';
      });
      this.updateAvailability(selected);
      const variant = this.variants.find((v) => v.options.every((value, index) => value === selected[index]));
      this.applyVariant(variant);
    }

    updateAvailability(selected) {
      this.querySelectorAll('.bm-opt').forEach((group, index) => {
        group.querySelectorAll('[data-bm-option]').forEach((input) => {
          const available = this.variants.some(
            (v) =>
              v.available &&
              v.options.every((value, j) => {
                if (j === index) return value === input.value;
                if (j < index) return value === selected[j];
                return true;
              })
          );
          const label = input.closest('label');
          if (label) label.classList.toggle('is-unavailable', !available);
        });
      });
    }

    applyVariant(variant) {
      const button = this.querySelector('[data-bm-atc]');
      const buttonLabel = this.querySelector('[data-bm-atc-label]');
      const buttonPrice = this.querySelector('[data-bm-atc-price]');

      if (!variant) {
        if (button) button.disabled = true;
        if (buttonLabel) buttonLabel.textContent = this.dataset.unavailable;
        if (buttonPrice) buttonPrice.textContent = '';
        return;
      }

      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.querySelectorAll('[data-bm-price]').forEach((price) => {
        price.textContent = variant.price;
        if (variant.compare) {
          const was = document.createElement('s');
          was.textContent = variant.compare;
          price.appendChild(was);
        }
      });

      if (button) {
        button.disabled = !variant.available;
        buttonLabel.textContent = variant.available ? this.dataset.addLabel : this.dataset.soldOut;
        buttonPrice.textContent = variant.available ? variant.price : '';
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      if (variant.media && this.gallery) {
        const item = this.gallery.querySelector(`[data-media-id="${variant.media}"]`);
        if (item && item !== this.gallery.firstElementChild) this.gallery.prepend(item);
        this.gallery.scrollTo({ left: 0, behavior: 'smooth' });
        if (mqDesktop.matches) {
          const top = this.gallery.getBoundingClientRect().top + window.scrollY - 100;
          if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
        }
        this.updateCounter();
      }

      document.dispatchEvent(new CustomEvent('bm:variant-change', { detail: { variant } }));
    }

    updateCounter() {
      if (!this.counter || !this.gallery) return;
      const width = this.gallery.clientWidth || 1;
      const index = Math.round(this.gallery.scrollLeft / width) + 1;
      this.counter.textContent = `${Math.min(index, this.gallery.children.length)} / ${this.gallery.children.length}`;
    }

    async onSubmit(event) {
      event.preventDefault();
      const button = this.querySelector('[data-bm-atc]');
      const error = this.querySelector('[data-bm-error]');
      if (!button || button.disabled) return;

      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }

      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = new FormData(this.form);
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.append('sections', cart.getSectionsToRender().map((section) => section.id));
        body.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }

      try {
        const response = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body,
        });
        const data = await response.json();

        if (data.status) {
          if (error) {
            error.textContent = data.description || data.message || 'This item could not be added to your cart.';
            error.hidden = false;
          }
          return;
        }

        if (window.publish && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'bm-product',
            productVariantId: body.get('id'),
            cartData: data,
          });
        }
        document.dispatchEvent(new CustomEvent('bm:cart-added', { detail: data }));

        if (cart && typeof cart.renderContents === 'function') {
          cart.classList.remove('is-empty');
          cart.renderContents(data);
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }
      } catch (err) {
        if (error) {
          error.textContent = 'Something went wrong. Please try again.';
          error.hidden = false;
        }
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }

    openZoom(item) {
      let viewer = document.querySelector('.bm-zoom');
      if (!viewer) {
        viewer = document.createElement('div');
        viewer.className = 'bm-zoom';
        viewer.setAttribute('role', 'dialog');
        viewer.setAttribute('aria-modal', 'true');
        viewer.setAttribute('aria-label', 'Product photos');
        viewer.addEventListener('click', (event) => {
          if (event.target.closest('.bm-zoom__close') || event.target.tagName === 'IMG') closeZoom();
        });
        document.body.appendChild(viewer);
      }

      viewer.innerHTML = `<button type="button" class="bm-zoom__close" aria-label="Close">${closeIcon}</button>`;
      const items = [...this.gallery.querySelectorAll('.bm-media[data-zoom-src]')];
      items.forEach((media) => {
        const img = document.createElement('img');
        img.src = media.dataset.zoomSrc;
        const source = media.querySelector('img');
        img.alt = source ? source.alt : '';
        viewer.appendChild(img);
      });

      viewer.bmTrigger = item.querySelector('img');
      viewer.classList.add('is-open');
      document.body.classList.add('bm-lock');
      const index = items.indexOf(item);
      const target = viewer.querySelectorAll('img')[index];
      requestAnimationFrame(() => {
        viewer.scrollTop = target ? target.offsetTop : 0;
      });
      viewer.querySelector('.bm-zoom__close').focus({ preventScroll: true });
    }
  }

  if (!customElements.get('bm-product')) customElements.define('bm-product', BmProduct);
})();
